/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Typography,
  Checkbox,
  ListItemButton,
  ListItemIcon,
  Box,
} from '@mui/material';
import React from 'react';
import type { syncGroupsTypes } from '@opengeoweb/store';

const styles = {
  listItem: {
    width: '80%',
    borderRadius: '5px',
  },
  isActive: {
    color: 'geowebColors.typographyAndIcons.icon',
  },
  checkboxIsActive: {
    color: 'geowebColors.typographyAndIcons.iconLinkActive',
  },
};

interface SyncGroupListItemProps {
  groupId: string;
  selected: string[];
  sourcesById: syncGroupsTypes.SourceById[];
  handleToggle: (id: string) => void;
}

export const SyncGroupListItem: React.FC<SyncGroupListItemProps> = ({
  groupId,
  selected,
  sourcesById,
  handleToggle,
}: SyncGroupListItemProps) => {
  return (
    <Box sx={styles.listItem}>
      {sourcesById.map((source) => {
        const isChecked = selected.includes(source.id);
        const isDisabledByGroup =
          source.disabledForGroupIds?.includes(groupId) || false;
        const isDisabled = Boolean(
          (source.disabled || isDisabledByGroup) && !isChecked,
        );
        return (
          <ListItemButton
            dense
            disabled={isDisabled}
            key={source.id}
            onClick={(): void => handleToggle(source.id)}
            sx={styles.listItem}
          >
            <ListItemIcon>
              <Checkbox
                checked={isChecked}
                color="default"
                disableRipple
                disabled={isDisabled}
                edge="start"
                slotProps={{ input: { 'aria-labelledby': source.name } }}
                tabIndex={-1}
              />
            </ListItemIcon>
            <Typography>{source.name}</Typography>
          </ListItemButton>
        );
      })}
    </Box>
  );
};
